import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  AssigneeBadge,
  Badge,
  Button,
  ContactAttemptStack,
  ContactAttemptsModal,
  Input,
  LeadPlatformCell,
  LeadStatusOptions,
  MultiSelect,
  Pagination,
  Select,
  Spinner,
  PHONE_COLUMN_IDS,
  RETRY_STATUSES,
  cn,
  computeLeadRange,
  formatAnswer,
  formatKstDateTime,
  formatPhone,
  pickPhone,
  statusColor,
  useAuth,
  useToast,
} from "@toss-ads/ui";
import {
  downloadPlatformXlsx,
  fetchAbsentLeads,
  fetchAssignees,
  fetchLeads,
  fetchMediaOptions,
  fetchPlatformSummaries,
  fetchSources,
  removeContactAttempt,
  setLeadAssignee,
  setLeadStatus,
} from "../lib/api";
import type { SourceMetaInfo } from "@toss-ads/ui";
import type { Lead, LeadStatus } from "../lib/types";
import { useUrlState } from "../lib/use-url-state";

const PER_PAGE = 20;
type DatePreset = "recent-1d" | "recent-7d" | "recent-30d" | "all" | "custom";

// 부재 계열 = 연락 재시도 대상 상태.
function isRetryStatus(s: LeadStatus): boolean {
  return (RETRY_STATUSES as readonly string[]).includes(s);
}

// 검색 입력 — 인풋은 로컬 state(한글 IME 조합 안 깨지게), URL 반영은 디바운스.
// committed 는 URL 동기화된(=디바운스된) 값으로 쿼리/필터에 사용.
function useSearchField(key: string): {
  input: string;
  setInput: (v: string) => void;
  committed: string;
} {
  const [urlValue, setUrlValue] = useUrlState(key, "");
  const [input, setInput] = React.useState(urlValue);
  React.useEffect(() => {
    const t = setTimeout(() => setUrlValue(input), 300);
    return () => clearTimeout(t);
  }, [input, setUrlValue]);
  // 뒤로가기 등 외부에서 URL 이 바뀌면 인풋에 반영(값이 다를 때만).
  React.useEffect(() => {
    setInput((cur) => (cur === urlValue ? cur : urlValue));
  }, [urlValue]);
  return { input, setInput, committed: urlValue };
}

export function LeadsPage({
  variant = "leads",
}: {
  variant?: "leads" | "absent";
} = {}) {
  const absent = variant === "absent";
  const toast = useToast();
  const { user } = useAuth();
  const meId = user?.id ?? null;

  const { data: summaries } = useQuery({
    queryKey: ["leads", "platform-summaries"],
    queryFn: fetchPlatformSummaries,
  });

  const platformMeta = React.useMemo(() => {
    const m = new Map<string, { name: string; logoUrl: string | null }>();
    for (const s of summaries ?? []) {
      const key = s.platform?.id ?? "organic";
      m.set(key, {
        name: s.platform?.name ?? "자연유입",
        logoUrl: s.platform?.logoUrl ?? null,
      });
    }
    return m;
  }, [summaries]);

  const { data: sources } = useQuery({
    queryKey: ["leads", "sources"],
    queryFn: fetchSources,
  });
  const sourceMeta = React.useMemo(() => {
    const m = new Map<string, SourceMetaInfo>();
    for (const s of sources ?? [])
      m.set(s.slug, { label: s.label });
    return m;
  }, [sources]);

  const { data: assignees } = useQuery({
    queryKey: ["leads", "assignees"],
    queryFn: () => fetchAssignees(),
  });

  // 필터·페이지는 URL 쿼리에 동기화 (뒤로가기/새로고침 복원).
  // media: 콤마로 여러 매체 키(빈 값 = 전체).
  const [media, setMedia] = useUrlState("media", "");
  const selectedMedia = React.useMemo(
    () => (media ? media.split(",").filter(Boolean) : []),
    [media],
  );
  const [presetRaw, setPreset] = useUrlState("date", "recent-30d");
  const preset = presetRaw as DatePreset;
  const [customFrom, setCustomFrom] = useUrlState("from", "");
  const [customTo, setCustomTo] = useUrlState("to", "");
  const [statusRaw, setStatusFilter] = useUrlState("status", "");
  const statusFilter = statusRaw as "" | LeadStatus;
  // "" 전체 | "me" 내것 | "unassigned" 미배정 | uuid
  const [assigneeFilter, setAssigneeFilter] = useUrlState("assignee", "");
  // 인입 DB 검색 — 인풋은 로컬(IME), URL/쿼리는 디바운스.
  const nameField = useSearchField("name");
  const contactField = useSearchField("contact");
  const debouncedName = nameField.committed;
  const debouncedContact = contactField.committed;
  const [pageStr, setPageStr] = useUrlState("page", "1");
  const page = Number(pageStr) || 1;
  const setPage = React.useCallback(
    (n: number) => setPageStr(String(n)),
    [setPageStr],
  );
  const [downloading, setDownloading] = React.useState(false);

  const range = React.useMemo(
    () => computeLeadRange(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );

  const { data: mediaOptions } = useQuery({
    queryKey: ["leads", "media-options", range],
    queryFn: () => fetchMediaOptions({ from: range.from, to: range.to }),
  });
  const grandTotal = (mediaOptions ?? []).reduce((acc, o) => acc + o.total, 0);

  // 필터가 "실제로 바뀌면" 1페이지로. 최초 마운트(새로고침 포함)엔 URL 의 page 보존.
  // firstRender ref 가드는 StrictMode(이펙트 2회)에서 새로고침 시 page 가 날아가므로,
  // 이전 필터 시그니처와 비교해 값이 바뀐 경우에만 리셋한다.
  const filterSig = [
    media,
    preset,
    customFrom,
    customTo,
    statusFilter,
    assigneeFilter,
    debouncedName,
    debouncedContact,
  ].join("\u0001");
  const prevFilterSig = React.useRef(filterSig);
  React.useEffect(() => {
    if (prevFilterSig.current === filterSig) return;
    prevFilterSig.current = filterSig;
    setPage(1);
  }, [filterSig, setPage]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      "leads",
      variant,
      media,
      range,
      absent ? "" : statusFilter,
      assigneeFilter,
      debouncedName,
      debouncedContact,
      page,
    ],
    queryFn: () => {
      const params = {
        media: selectedMedia.length ? media : undefined,
        from: range.from,
        to: range.to,
        // 부재 탭은 서버가 부재/단선으로 강제 → status 전달 안 함.
        status: absent ? undefined : statusFilter || undefined,
        assignee: assigneeFilter || undefined,
        name: debouncedName || undefined,
        contact: debouncedContact || undefined,
        isTest: false,
        limit: PER_PAGE,
        offset: (page - 1) * PER_PAGE,
      };
      return absent ? fetchAbsentLeads(params) : fetchLeads(params);
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const customRangeValid =
    preset !== "custom" || (!!customFrom && !!customTo);

  async function handleDownload() {
    setDownloading(true);
    try {
      // 매체 필터를 반영해 다운로드 (path 는 전체, media 쿼리로 스코핑).
      await downloadPlatformXlsx("all", {
        from: range.from,
        to: range.to,
        isTest: false,
        media: selectedMedia.length ? media : undefined,
      });
      toast.success("다운로드 완료");
    } catch (e) {
      toast.error("다운로드 실패. 다시 시도해주세요");
      console.error(e);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-[18px] font-semibold tracking-tight">
          {absent ? "부재 고객" : "잠재고객 데이터"}
        </h1>
        {absent && (
          <p className="text-[12px] text-ink-subtle mt-1">
            부재·단선·지속부재·상담후 부재·문발완료 상태이면서 연락 시도 7회
            미만인 고객. 상태가 바뀌거나 7회에 도달하면 목록에서 자동
            제외됩니다.
          </p>
        )}
      </div>

      <div className="rounded-lg border bg-surface p-5 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <MultiSelect
            allLabel={`매체 전체 (${grandTotal})`}
            options={(mediaOptions ?? []).map((o) => ({
              value: o.key,
              label: `${o.label} (${o.total})`,
            }))}
            selected={selectedMedia}
            onChange={(next) => setMedia(next.join(","))}
          />

          <Select
            value={preset}
            onChange={(e) => setPreset(e.target.value as DatePreset)}
          >
            <option value="recent-1d">최근 1일</option>
            <option value="recent-7d">최근 7일</option>
            <option value="recent-30d">최근 30일</option>
            <option value="all">전체 기간</option>
            <option value="custom">날짜 지정</option>
          </Select>

          {preset === "custom" && (
            <>
              <Input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="w-[160px] h-[34px]"
              />
              <span className="text-[12px] text-ink-subtle">~</span>
              <Input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="w-[160px] h-[34px]"
              />
            </>
          )}

          {!absent && (
            <Select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as "" | LeadStatus)
              }
            >
              <option value="">처리 상태 전체</option>
              <LeadStatusOptions />
            </Select>
          )}

          <Select
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
          >
            <option value="">담당자 전체</option>
            <option value="me">내 담당</option>
            <option value="unassigned">미배정</option>
            {(assignees ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.note || a.email}
              </option>
            ))}
          </Select>

          <Input
            value={nameField.input}
            onChange={(e) => nameField.setInput(e.target.value)}
            placeholder="이름 검색"
            className="w-[130px] h-[34px]"
          />
          <Input
            value={contactField.input}
            onChange={(e) => contactField.setInput(e.target.value)}
            placeholder="연락처 검색"
            className="w-[140px] h-[34px]"
          />

          <Button
            size="md"
            className="ml-auto"
            loading={downloading}
            disabled={!customRangeValid}
            onClick={() => void handleDownload()}
          >
            엑셀 다운로드
          </Button>
        </div>

        {preset === "custom" && !customRangeValid && (
          <p className="text-[11px] text-ink-subtle mt-2">
            시작일과 종료일을 모두 선택해주세요.
          </p>
        )}
      </div>

      <div className="rounded-lg border bg-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-muted">
            <tr>
              <Th width={48}>No.</Th>
              <Th width={170}>제출 시각</Th>
              <Th width={176}>플랫폼</Th>
              <Th width={130}>처리 상태</Th>
              <Th width={130}>연락 시도</Th>
              <Th width={200}>담당자</Th>
              <Th width={160}>휴대전화</Th>
              <Th>응답</Th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={8} className="py-16">
                  <div className="flex justify-center text-ink-subtle">
                    <Spinner size={20} />
                  </div>
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={8} className="py-12 text-center text-[13px] text-danger">
                  {error instanceof ApiError
                    ? error.message
                    : "데이터를 불러오지 못했습니다."}
                </td>
              </tr>
            )}
            {!isLoading && !isError && items.length === 0 && (
              <tr>
                <td colSpan={8} className="py-12 text-center text-[13px] text-ink-subtle">
                  선택한 조건에 해당하는 잠재고객이 없습니다.
                </td>
              </tr>
            )}
            {items.map((lead) => (
              <LeadRow
                key={lead.id}
                lead={lead}
                no={lead.dateNo}
                meId={meId}
                platformMeta={platformMeta}
                sourceMeta={sourceMeta}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center mt-4">
        <Pagination
          total={total}
          perPage={PER_PAGE}
          current={page}
          onChange={setPage}
        />
      </div>
    </div>
  );
}

function LeadRow({
  lead,
  no,
  meId,
  platformMeta,
  sourceMeta,
}: {
  lead: Lead;
  no: number;
  meId: string | null;
  platformMeta: Map<string, { name: string; logoUrl: string | null }>;
  sourceMeta: Map<string, SourceMetaInfo>;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const mutate = useMutation({
    mutationFn: (next: LeadStatus) => setLeadStatus(lead.id, next),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      toast.success("상태가 변경되었습니다");
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "변경 실패");
    },
  });
  const assign = useMutation({
    mutationFn: (assigneeId: string | null) =>
      setLeadAssignee(lead.id, assigneeId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leads"] }),
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "배정 실패"),
  });
  const [attemptsOpen, setAttemptsOpen] = React.useState(false);
  const removeAttempt = useMutation({
    mutationFn: (at: string) => removeContactAttempt(lead.id, at),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leads"] }),
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "삭제 실패"),
  });
  // +1 재시도는 별도 mutation — 상태셀 스피너(레이아웃 이동) 안 건드림.
  const retry = useMutation({
    mutationFn: () => setLeadStatus(lead.id, lead.status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leads"] }),
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "재시도 실패"),
  });

  const phone = pickPhone(lead);
  const mine = !!meId && lead.assigneeId === meId;

  return (
    <tr className="border-t border-border align-top">
      <Td className="text-ink-subtle tabular-nums">{no}</Td>
      <Td className="text-ink">{formatKstDateTime(lead.leadSubmitTime)}</Td>
      <Td>
        <LeadPlatformCell
          source={lead.source}
          platformId={lead.platformId}
          channel={lead.channel}
          platformMeta={platformMeta}
          sourceMeta={sourceMeta}
        />
      </Td>
      <Td>
        <div className="flex items-center gap-1.5">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: statusColor(lead.status) }}
            aria-hidden
          />
          <Select
            value={lead.status}
            onChange={(e) => mutate.mutate(e.target.value as LeadStatus)}
            disabled={mutate.isPending}
            className="w-[130px]"
          >
            <LeadStatusOptions />
          </Select>
          {/* 스피너 자리 항상 예약 — 나타났다 사라질 때 레이아웃 안 밀림. */}
          <span className="w-3 shrink-0 flex items-center">
            {mutate.isPending && <Spinner size={12} />}
          </span>
        </div>
      </Td>
      <Td>
        {lead.contactAttempts.length > 0 || isRetryStatus(lead.status) ? (
          <div className="flex flex-col gap-1 items-start">
            {lead.contactAttempts.length > 0 && (
              <button
                type="button"
                className="cursor-pointer hover:opacity-80"
                title="시도 내역 보기"
                onClick={() => setAttemptsOpen(true)}
              >
                <ContactAttemptStack
                  attempts={lead.contactAttempts}
                  showLastTime
                />
              </button>
            )}
            {isRetryStatus(lead.status) && (
              <button
                type="button"
                className="text-[11px] text-primary hover:underline disabled:opacity-50"
                disabled={retry.isPending}
                onClick={() => retry.mutate()}
              >
                +1 재시도
              </button>
            )}
          </div>
        ) : (
          <span className="text-ink-subtle text-[12px]">—</span>
        )}
        <ContactAttemptsModal
          open={attemptsOpen}
          onClose={() => setAttemptsOpen(false)}
          attempts={lead.contactAttempts}
          meId={meId}
          onDelete={(at) => removeAttempt.mutate(at)}
          deletingAt={removeAttempt.isPending ? removeAttempt.variables : null}
        />
      </Td>
      <Td>
        <div className="flex items-center gap-1.5">
          {lead.assigneeId ? (
            <>
              <AssigneeBadge
                label={
                  mine
                    ? "나"
                    : lead.assigneeNote || lead.assigneeEmail
                }
                color={lead.assigneeColor}
              />
              {mine && (
                <button
                  type="button"
                  className="text-[11px] text-ink-subtle hover:text-danger"
                  disabled={assign.isPending}
                  onClick={() => assign.mutate(null)}
                >
                  해제
                </button>
              )}
            </>
          ) : (
            <Button
              size="sm"
              variant="outline"
              disabled={!meId || assign.isPending}
              onClick={() => meId && assign.mutate(meId)}
            >
              나에게 배정
            </Button>
          )}
          {assign.isPending && <Spinner size={12} />}
        </div>
      </Td>
      <Td>
        {phone ? (
          <a
            href={`tel:${phone}`}
            className="font-mono text-[13px] text-primary hover:underline no-underline"
          >
            {formatPhone(phone)}
          </a>
        ) : (
          <span className="text-ink-subtle text-[12px]">—</span>
        )}
      </Td>
      <Td>
        <SubmittedFull lead={lead} />
      </Td>
    </tr>
  );
}

function SubmittedFull({ lead }: { lead: Lead }) {
  const ucd = (
    Array.isArray(lead.userColumnData)
      ? (lead.userColumnData as UserColumn[])
      : []
  ).filter(
    (c) => !c?.column_id || !PHONE_COLUMN_IDS.includes(c.column_id.toLowerCase()),
  );
  const items = Array.isArray(lead.submittedContent)
    ? (lead.submittedContent as Array<{ question?: string; answer?: unknown }>)
    : [];
  if (ucd.length === 0 && items.length === 0) {
    return <span className="text-[12px] text-ink-subtle">응답 없음</span>;
  }
  return (
    <div className="space-y-2">
      {ucd.map((c, idx) => (
        <div key={`u${idx}`} className="text-[12px]">
          <div className="text-ink-subtle whitespace-pre-wrap break-words">
            {c.column_name ?? c.column_id ?? "필드"}
          </div>
          <div className="text-ink whitespace-pre-wrap break-words mt-0.5">
            {c.string_value == null || c.string_value === ""
              ? "—"
              : String(c.string_value)}
          </div>
        </div>
      ))}
      {items.map((q, idx) => (
        <div key={`q${idx}`} className="text-[12px]">
          <div className="text-ink-subtle whitespace-pre-wrap break-words">
            Q. {q.question ?? `질문${idx + 1}`}
          </div>
          <div className="text-ink whitespace-pre-wrap break-words mt-0.5">
            {formatAnswer(q.answer)}
          </div>
        </div>
      ))}
    </div>
  );
}

interface UserColumn {
  column_id?: string;
  column_name?: string;
  string_value?: string | number | null;
}

function Th({
  children,
  width,
}: {
  children: React.ReactNode;
  width?: number;
}) {
  return (
    <th
      style={{ width }}
      className="px-2.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-subtle border-b border-border whitespace-nowrap"
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <td className={cn("px-2.5 py-3.5 align-middle", className)}>{children}</td>
  );
}

